// LICENSE_CODE ZON
/*jslint browser:true, es6:true, node:true, react:true*/
import React, {useEffect, useMemo, useRef, useState} from 'react';
import cn from 'classnames';
import {use_theme} from '../../../../hola/pub/settings_ctx';
import {Flag} from '../../../../hola/pub/lib/flag';
import Icon_x from '../../../pub/img/icons/16/close.svg';
import Icon_chevron_down from '../../../../hola/pub/img/svg/icon/chevron_down.svg';
import Icon_flag from '../../../../oculus/pub/img/icons/24/flag.svg';
import Icon_triangle_up from '../../../../oculus/pub/img/icons/24/triangle_up.svg';
import Icon_triangle_down from '../../../../oculus/pub/img/icons/24/triangle_down.svg';

import styles from './country_selector_v2.module.less';

export type t_country = {
    code: string;
    name: string;
    is_empty_option?: boolean;
};

interface i_country_selector_v2_props {
    country?: string;
    on_country_change: (next_value: string)=>void;
    all: t_country[];
    cls?: string;
    item_cls?: string;
    label?: string;
    with_filter?: boolean;
    with_empty_option?: boolean;
    render_flag?: (country: string)=>JSX.Element;
}

export const Country_selector_v2 = (props: i_country_selector_v2_props)=>{
    const {all, on_country_change, country, cls, item_cls, label,
        with_filter = true, with_empty_option} = props;
    const {theme, theme_name} = use_theme();
    const is_oculus = theme_name == 'oculus';
    const [is_open, set_is_open] = useState(false);
    const [search_text, set_search_text] = useState('');
    const [focused_index, set_focused_index] = useState(-1);
    const input_ref = useRef<HTMLInputElement>(null);
    const dropdown_ref = useRef<HTMLDivElement>(null);
    const container_ref = useRef<HTMLDivElement>(null);
    const selected_country = useMemo(()=>{
        if (!country)
            return null;
        return all.find(c=>c.code == country) || null;
    }, [country, all]);
    const filtered_countries = useMemo(()=>{
        let countries_to_show = all;
        if (!search_text || !with_filter)
            return countries_to_show;
        const search_lower = search_text.toLowerCase();
        const filtered = countries_to_show.filter(c=>{
            if (c.is_empty_option)
                return false;
            return c.name.toLowerCase().includes(search_lower) ||
                  c.code.toLowerCase().includes(search_lower);
        });
        return filtered;
    }, [all, search_text, with_filter]);
    useEffect(()=>{
        const handle_click_outside = (event: MouseEvent)=>{
            if (container_ref.current &&
              !container_ref.current.contains(event.target as Node))
            {
                set_is_open(false);
                set_search_text('');
                set_focused_index(-1);
            }
        };
        document.addEventListener('mousedown', handle_click_outside);
        return ()=>document.removeEventListener('mousedown',
            handle_click_outside);
    }, []);
    const handle_key_down = (e: React.KeyboardEvent)=>{
        if (!is_open)
        {
            if (e.key == 'Enter' || e.key == ' ' || e.key == 'ArrowDown')
            {
                e.preventDefault();
                set_is_open(true);
                set_focused_index(0);
            }
            return;
        }
        switch (e.key)
        {
            case 'ArrowDown':
                e.preventDefault();
                set_focused_index(prev=>
                    prev < filtered_countries.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                set_focused_index(prev=>
                    prev > 0 ? prev - 1 : filtered_countries.length - 1
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (focused_index >= 0 &&
                    focused_index < filtered_countries.length)
                {
                    const selected = filtered_countries[focused_index];
                    handle_select(selected);
                }
                break;
            case 'Escape':
                set_is_open(false);
                set_search_text('');
                set_focused_index(-1);
                input_ref.current?.blur();
                break;
        }
    };
    const handle_select = (country_selected: t_country)=>{
        on_country_change(country_selected.code);
        set_is_open(false);
        set_search_text('');
        set_focused_index(-1);
        if (input_ref.current)
            input_ref.current.blur();
    };
    const handle_clear = (e: React.MouseEvent)=>{
        e.stopPropagation();
        if (with_empty_option)
          on_country_change('');
        set_is_open(false);
        set_search_text('');
        set_focused_index(-1);
    };
    const handle_input_click = ()=>{
        if (!selected_country)
        {
            set_is_open(true);
            if (input_ref.current)
                input_ref.current.focus();
        }
    };
    const handle_chevron_click = (e: React.MouseEvent)=>{
        e.preventDefault();
        set_is_open(!is_open);
        if (!is_open)
            set_search_text('');
        if (!is_open && input_ref.current)
            input_ref.current.focus();
    };
    const get_display_value = ()=>{
        if (is_open && with_filter)
            return search_text;
        if (selected_country)
            return selected_country.name;
        return '';
    };
    const get_placeholder = ()=>{
        if (with_empty_option && !country)
            return 'Choose country';
        return '';
    };
    const highlight_text = (text: string, search: string)=>{
        if (!search || !with_filter)
            return text;
        const parts = text.split(new RegExp(`(${search})`, 'gi'));
        return parts.map((part, index)=>{
            if (part.toLowerCase() == search.toLowerCase())
            {
                return <span key={index} className={styles.highlight}>{part}
                  </span>;
            }
            return part;
        });
    };
    return <div className={cn(styles.country_selector, cls)}
      ref={container_ref}>
      {label &&
        <span className={cn(
          styles.label,
          is_oculus && styles.label_oculus,
          theme.P,
          is_oculus ? theme.p14 : theme.p4,
          is_oculus ? theme.med : theme.reg
        )}>
          {label}
        </span>
      }
      <div className={cn(
        is_oculus ? styles.input_container_oculus : styles.input_container,
        is_open && styles.open,
        is_oculus && is_open && styles.open_oculus
      )}>
        <div className={styles.flag_container}>
          {selected_country && (!is_open || !search_text) ?
            props.render_flag?.(selected_country.code) ??
            <Flag country={selected_country.code} size={24} round />
            :
            is_oculus ?
              <Icon_flag className={styles.empty_flag_oculus} /> :
              <Flag country='empty' size={24} round />
          }
        </div>
        <input
          ref={input_ref}
          type='text'
          className={cn(styles.input, theme.P, theme.p14, theme.med)}
          value={get_display_value()}
          placeholder={get_placeholder()}
          onChange={e=>set_search_text(e.target.value)}
          onClick={handle_input_click}
          onKeyDown={handle_key_down}
          autoComplete='off'
          readOnly={selected_country && !is_open}
        />
        {selected_country && with_empty_option ?
          <button
            className={is_oculus ? styles.clear_button_oculus :
              styles.clear_button}
            onClick={handle_clear}
            type='button'
          >
            <Icon_x width={16} height={16} />
          </button>
          :
          <button
            className={cn(styles.chevron_button, is_oculus &&
              styles.chevron_button_oculus)}
            onClick={handle_chevron_click}
            type='button'
          >
            {is_oculus ?
              is_open ?
                <Icon_triangle_up className={styles.triangle_icon} /> :
                <Icon_triangle_down className={styles.triangle_icon} />
              :
                <Icon_chevron_down
                  width={24}
                  height={24}
                  className={cn(styles.chevron, is_open && styles.rotated)}
                />
            }
          </button>
        }
      </div>
      {is_open &&
        <div className={cn(is_oculus ? styles.dropdown_oculus : styles.dropdown
          )} ref={dropdown_ref}>
          <ul className={styles.options_list}>
            {filtered_countries.map((country_item, index)=>
              <li
                key={country_item.code || 'empty'}
                className={cn(
                  styles.option,
                  item_cls,
                  index == focused_index && styles.focused,
                  country_item.code == country && styles.selected
                )}
                onClick={()=>handle_select(country_item)}
                onMouseEnter={()=>set_focused_index(index)}
              >
                <div className={cn(styles.option_content,
                  is_oculus && styles.option_content_oculus)}>
                  {props.render_flag?.(country_item.code || 'empty') ??
                  <Flag country={country_item.code || 'empty'} size={24}
                    round />}
                  <p className={cn(is_oculus ? theme.p16 : theme.p12,
                    theme.reg)}>
                    {highlight_text(country_item.name, search_text)}
                  </p>
                </div>
              </li>
            )}
            {filtered_countries.length == 0 &&
              <li className={cn(styles.option, styles.no_results)}>
                <p className={cn(theme.p12, theme.reg)}>No countries found</p>
              </li>
            }
          </ul>
        </div>
      }
    </div>;
};
